import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Wrench, Play, Square, RotateCcw, CheckCircle2, XCircle, MinusCircle, Clock, Loader2 } from "lucide-react";

const STATUS_META = {
    ok: { icon: CheckCircle2, cls: "text-emerald-400", label: "ok" },
    failed: { icon: XCircle, cls: "text-red-400", label: "failed" },
    timeout: { icon: Clock, cls: "text-amber-400", label: "timeout" },
    skipped: { icon: MinusCircle, cls: "text-zinc-500", label: "skipped" },
};

export default function FleetRepairPanel() {
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [includeExpensive, setIncludeExpensive] = useState(true);
    const pollRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/api/admin/fleet/repair/status");
            setRun(r.data.run || null);
        } catch (e) {
            // silent — panel is best-effort
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Poll while a run is active.
    useEffect(() => {
        const isRunning = run && run.status === "running";
        if (isRunning && !pollRef.current) {
            pollRef.current = setInterval(load, 3000);
        } else if (!isRunning && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [run, load]);

    const start = async () => {
        if (!confirm(
            "Start fleet repair?\n\n" +
            `Runs "occ maintenance:repair${includeExpensive ? " --include-expensive" : ""}" ` +
            "sequentially on ALL active, installed clouds (one after another). " +
            "This can take a while depending on the number of clouds. Continue?")) return;
        setBusy(true);
        try {
            const r = await api.post("/api/admin/fleet/repair/start", { include_expensive: includeExpensive });
            setRun(r.data.run);
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(false);
        }
    };
    const cancel = async () => {
        if (!confirm("Cancel the running pass? The currently running cloud will still be finished.")) return;
        setBusy(true);
        try { await api.post("/api/admin/fleet/repair/cancel"); await load(); }
        catch (e) { alert(e?.response?.data?.detail || String(e)); }
        finally { setBusy(false); }
    };
    const resume = async () => {
        setBusy(true);
        try { const r = await api.post("/api/admin/fleet/repair/resume"); setRun(r.data.run); }
        catch (e) { alert(e?.response?.data?.detail || String(e)); }
        finally { setBusy(false); }
    };

    const st = run?.status;
    const running = st === "running";
    const pct = run && run.total ? Math.round((run.done / run.total) * 100) : 0;

    return (
        <div className="border border-zinc-800 bg-zinc-950" data-testid="fleet-repair-panel">
            <div className="px-4 py-3 border-b border-zinc-800 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Wrench size={16} className="text-cm-brand" />
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-200">
                        Fleet repair — maintenance:repair
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {!running && (
                        <label className="flex items-center gap-1.5 text-xs text-zinc-400 mr-1">
                            <input
                                type="checkbox"
                                data-testid="fleet-repair-expensive"
                                checked={includeExpensive}
                                onChange={(e) => setIncludeExpensive(e.target.checked)}
                                className="accent-[#CCFF00]"
                            />
                            --include-expensive
                        </label>
                    )}
                    {(st === "interrupted" || st === "canceled") && (
                        <button
                            data-testid="fleet-repair-resume-btn"
                            onClick={resume}
                            disabled={busy}
                            className="px-3 py-2 text-xs font-mono uppercase tracking-widest bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-40 flex items-center gap-2"
                        >
                            <RotateCcw size={12} /> Resume
                        </button>
                    )}
                    {running ? (
                        <button
                            data-testid="fleet-repair-cancel-btn"
                            onClick={cancel}
                            disabled={busy}
                            className="px-4 py-2 text-xs font-mono uppercase tracking-widest bg-red-700 text-white hover:bg-red-600 disabled:opacity-40 flex items-center gap-2"
                        >
                            <Square size={12} /> Cancel
                        </button>
                    ) : (
                        <button
                            data-testid="fleet-repair-start-btn"
                            onClick={start}
                            disabled={busy}
                            className="px-4 py-2 text-xs font-mono uppercase tracking-widest bg-[#CCFF00] text-[#05070A] hover:bg-[#9FCC00] disabled:opacity-40 flex items-center gap-2"
                        >
                            <Play size={12} /> {busy ? "…" : "Repair all clouds"}
                        </button>
                    )}
                </div>
            </div>

            <div className="px-4 py-3 space-y-3">
                <p className="text-xs text-zinc-500 max-w-3xl">
                    Runs <b>cloud by cloud</b> (strictly sequential, DB-friendly) across all clouds with
                    <code className="text-zinc-400"> status=active</code> and installed Nextcloud. Stopped/suspended
                    clouds are skipped. The run can be canceled and resumed after a backend restart.
                </p>

                {loading ? (
                    <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading status…</div>
                ) : !run ? (
                    <div className="text-xs text-zinc-500" data-testid="fleet-repair-empty">No run started yet.</div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs font-mono" data-testid="fleet-repair-summary">
                            <span className={
                                running ? "text-[#5b8cff]" :
                                st === "completed" ? "text-emerald-400" :
                                st === "canceled" ? "text-zinc-400" :
                                st === "interrupted" ? "text-amber-400" : "text-zinc-300"
                            }>
                                Status: {running && <Loader2 size={11} className="inline animate-spin mr-1" />}{st}
                            </span>
                            <span className="text-zinc-300" data-testid="fleet-repair-progress">{run.done}/{run.total} ({pct}%)</span>
                            <span className="text-emerald-400">✓ {run.succeeded}</span>
                            <span className="text-red-400">✗ {run.failed}</span>
                            <span className="text-zinc-500">⤼ {run.skipped}</span>
                        </div>

                        <div className="h-2 w-full bg-zinc-900 overflow-hidden rounded-sm">
                            <div
                                className={`h-full transition-all ${st === "completed" ? "bg-emerald-500" : st === "canceled" || st === "interrupted" ? "bg-amber-500" : "bg-[#CCFF00]"}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>

                        {running && run.current_cloud_name && (
                            <div className="text-xs text-[#5b8cff] font-mono" data-testid="fleet-repair-current">
                                ▶ current: {run.current_cloud_name}
                            </div>
                        )}

                        {(run.results || []).length > 0 && (
                            <div className="border border-zinc-900 max-h-64 overflow-y-auto divide-y divide-zinc-900" data-testid="fleet-repair-results">
                                {run.results.map((r, i) => {
                                    const m = STATUS_META[r.status] || STATUS_META.failed;
                                    const Icon = m.icon;
                                    return (
                                        <div key={`${r.cloud_id}-${i}`} className="flex items-start gap-2 px-3 py-1.5 text-xs font-mono" data-testid={`fleet-repair-result-${r.cloud_id}`}>
                                            <Icon size={13} className={`${m.cls} mt-0.5 shrink-0`} />
                                            <span className="text-zinc-300 min-w-[200px] truncate">{r.name}</span>
                                            <span className={`${m.cls} uppercase min-w-[90px]`}>{m.label}</span>
                                            {typeof r.duration_ms === "number" && <span className="text-zinc-600">{(r.duration_ms / 1000).toFixed(1)}s</span>}
                                            {r.error && <span className="text-zinc-500 truncate flex-1" title={r.error}>· {r.error}</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
